<template>
  <div
    v-if="sourceId"
    :id="zoneId"
    class="hkld-dropzone"
  >
    <VueDropzone
      v-if="dropzoneHeaders"
      :id="dropzoneId"
      :key="dropzoneVueKey"
      ref="vueDropzone"
      :url="`${baseURL}/${customUrl || 'docs'}/`"
      :timeout="timeoutInMilli"
      :maxFileSizeInMB="docUploadSize"
      :maxNumberOfFiles="dropzoneMaxNumberOfFiles"
      :acceptedFileTypes="docUploadTypes"
      :headers="dropzoneHeaders"
      :autoProcessQueue="dropzoneAutoProcess"
      :previewTemplate="template"
      :language="DzLanguage"
      :hiddenInputContainer="hiddenInputContainer"
      @vdropzone-files-added="filesAddedAction"
      @vdropzone-sending="preSendRequest"
      @vdropzone-success="uploadSuccess"
      @vdropzone-error="setErrorMessage"
      @vdropzone-queue-complete="queueComplete"
    >
      <slot>
        <div
          :id="`dropzone-content-${sourceId}`"
          class="dz-default dz-message clearfix"
        >
          <img
            class="upload-img"
            src="/cloud-upload.svg"
            alt="Upload"
          >
          <span class="upload-text inputContainer">{{ $t('tender.attachment.uploadHint') }}</span>
          <div
            v-if="uploadNoteRows.length"
            class="hkld-dropzone__note"
          >
            <div
              v-for="noteRow in uploadNoteRows"
              :key="noteRow"
            >
              {{ noteRow }}
            </div>
          </div>
        </div>
      </slot>
      <input
        type="hidden"
        name="token"
        value="file upload"
      >
    </VueDropzone>
    <div
      v-show="totalCount"
      :class="{'hkld-dropzone__uploading': totalCount > 0}"
      style="color: #0C3A58"
    >
      <span>{{ totalCount }} {{ $t('tender.attachment.filesUploading') }}</span>
    </div>
    <div
      v-show="uploadResult"
      :class="{'hkld-dropzone__upload-result': !!uploadResult}"
      style="color: #0C3A58"
    >
      <span>{{ uploadResult }}</span>
    </div>
    <div
      v-show="errorMessage"
      :class="{'hkld-dropzone__error-message': !!errorMessage}"
    >
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
/*
* The vue2-dropzone bases on the dropzone.js libary. However there is some confuse logic in the dropzone.js
* When the user drops file(s) to the drop zone, the event triggered sequence is `addfiles` -> `addfile`
* When the user select file(s) by click, the event triggered sequence is `addfile` -> `addfiles`
* The "queuecomplete" event is triggered when added files and uploading files and queued files are zero.
* If a file has error before uploading, then the `error` event and `queuecomplete` are triggered in `addfile` event logic. Because the file isn't added to files queue. Then, the first `queuecomplete` events is triggered.
* After this event, if the rest files can be uploaded, the dropzone.js emit `success` event and `queuecomplete` event in sequence.
* Hence, there are twice `queuecomplete` event in one upload
* Document: https://www.npmjs.com/package/vue2-dropzone/v/2.3.4
*/
import VueDropzone from 'vue2-dropzone'
import lodash from 'lodash'
import { mapGetters } from 'vuex'
import EventBus from '@/utils/eventBus'
import { SETTING_CODE } from '@/api/settings/consts'
import { useBaseUrl } from '@/utils/http/hook'
import { FILE_TRANSFER_TIMEOUT } from '@/utils/consts'
import genRandomID from '@/utils/genRandomID'
import httpHeaderMixin from './httpHeaderMixin'

/**
 * Drop and drag zone to upload document
 * @type {import('vue').Component}
 * @props {Object} [metaData] the metaData field of the uploaded document
 * @example
 * ```jsx
 * <HkldDropzone :metaData="{uploadOnTop: true}" />
 * ```
 */
export default {
  name: 'HkldDropzone',
  components: {
    VueDropzone,
  },
  mixins: [httpHeaderMixin],

  props: {
    sourceId: {
      type: String,
      required: true,
    },
    metaData: {
      type: Object,
      required: false,
      default: undefined,
    },
    action: {
      type: String,
      default: '',
    },
    uploadDocTypes: {
      type: String,
      default: '',
    },
    uploadDocMaxSize: {
      type: Number,
      default: 0,
    },
    maxNumberOfFiles: {
      type: Number,
      default: 100,
    },
    autoProcess: {
      type: Boolean,
      default: true,
    },
    customUrl: {
      type: String,
      default: '',
    },
    existingFiles: {
      type: Array,
      default: () => [],
    },
    maxTotalFileSizeInBytes: {
      type: Number,
      default: 0,
    },
    uploadNoteRows: {
      type: Array,
      default: () => [],
    },
    filesExceedMessage: {
      type: String,
      default: '',
    },
    totalSizeExceedMessage: {
      type: String,
      default: '',
    },
    useTotalSizeMessageForFileSizeError: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    const zoneId = `hkld-dropzone-${genRandomID()}`
    return {
      dropzoneId: `dropzone-${genRandomID()}`,
      timeoutInMilli: FILE_TRANSFER_TIMEOUT || 30000,
      errorMessage: '',
      successCount: 0,
      errorCount: 0,
      totalCount: 0,
      uploadedFiles: [],
      uploadResult: '',
      prevSourceId: '',
      dropzoneHeaders: null,
      baseURL: useBaseUrl(),
      zoneId,
      hiddenInputContainer: `#${zoneId} .inputContainer`,
    }
  },

  computed: {
    ...mapGetters('settings', {
      getSettingByCode: 'setting',
    }),
    ...mapGetters('user', ['currentUser']),
    ...mapGetters('regions', ['currentRegion']),
    ...mapGetters('language', ['currentLanguage']),
    dropzoneVueKey () {
      return `${new Date().getTime()}-${this.currentLanguage}`
    },
    docUploadSize () {
      return this.uploadDocMaxSize || parseInt(this.getSettingByCode(SETTING_CODE.docUploadSize), 10) || 10
    },
    docUploadTypes () {
      return this.uploadDocTypes || this.getSettingByCode(SETTING_CODE.docUploadTypes) || 'application/pdf,image/png,image/jpeg,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    hasCumulativeUploadValidation () {
      return this.maxTotalFileSizeInBytes > 0 ||
        !!this.filesExceedMessage ||
        !!this.totalSizeExceedMessage ||
        this.uploadNoteRows.length > 0
    },
    dropzoneAutoProcess () {
      return this.autoProcess && !this.hasCumulativeUploadValidation
    },
    dropzoneMaxNumberOfFiles () {
      return this.hasCumulativeUploadValidation ? Number.MAX_SAFE_INTEGER : this.maxNumberOfFiles
    },
    DzLanguage () {
      return {
        dictFileTooBig: this.$t('hint.vueDropZone.fileTooBig'),
        dictMaxFilesExceeded: this.$t('hint.vueDropZone.maxFilesExceeded'),
        dictInvalidFileType: this.$t('hint.vueDropZone.invalidFileType'),
      }
    },
  },

  watch: {
    sourceId (val) {
      this.setupListener()
      this.uploadResult = ''
    },
  },
  async mounted () {
    this.dropzoneHeaders = await this.getHeaders()
    this.setupListener()
  },

  destroyed () {
    if (this.sourceId) {
      EventBus.$off(`controls.hkldDropzone.deleteItem.${this.sourceId}`)
    }
  },

  methods: {
    preSendRequest (file, xhr, formData) {
      formData.append('src', this.sourceId)
      // Add meta data
      for (const metaName in this.metaData) {
        if (this.metaData[metaName] === undefined) continue
        formData.append(metaName, this.metaData[metaName])
      }
      xhr.ontimeout = (e) => {
        this.appendErrorMessage(`${file.name} ${this.$t('hint.timeout')}!`)
        this.removeDropzoneFile(file)
        EventBus.$emit('module_loading_stopLoading')
        EventBus.$emit(`controls.hkldDropzone.timeout.${this.sourceId}`)
      }
    },

    queueComplete () {
      this.uploadResult = this.$t('hint.fileUploadSuccess', [this.successCount, this.totalCount])
      const queueFiles = this.$refs.vueDropzone.getQueuedFiles()
      // If files queue is empty, it mean this upload finished, we can reset the hint and related variable
      if (!queueFiles.length) {
        this.successCount = 0
        this.errorCount = 0
        this.totalCount = 0
      }
      EventBus.$emit('module_loading_stopLoading')
      // There are 3 instances in the code where the `controls-hkld-dropzone-complete` event is emitted. As a result, the scenario mentioned in the above comments(lines 67-73) may occur.
      EventBus.$emit('controls-hkld-dropzone-complete')
      EventBus.$emit(`controls.hkldDropzone.complete.${this.sourceId}`)
      this.$emit(`onCompleteQueue`, this.uploadedFiles)
    },

    template () {
      return `<div>
                <div class="dz-error-message"><span data-dz-errormessage></span></div>
              </div>`
    },

    async getHeaders () {
      let headers = {}
      if (this.action) {
        headers = { 'X-CMS-Action': this.action }
      }
      return this.getHttpHeaders(headers)
    },

    setErrorMessage (file, message) {
      if (this.shouldUseTotalSizeMessageForFileSizeError(file)) {
        this.removeDropzoneFile(file)
        EventBus.$emit('controls-hkld-dropzone-complete')
        this.errorMessage = this.totalSizeExceedMessage
        return
      }

      const isFileError = file.status === 'error'
      const errorMsgPrefix = isFileError ? this.$t('hint.vueDropZone.fileUploadError', [file.name]) : ''
      const errorMsg = isFileError
        ? typeof message === 'string'
          ? message
          : lodash.get(message, 'error') || $(file.previewElement).find('.dz-error-message').text()
        : ''

      this.removeDropzoneFile(file)
      EventBus.$emit('controls-hkld-dropzone-complete')
      if (isFileError) {
        setTimeout(() => {
          this.appendErrorMessage(`${errorMsgPrefix}: ${errorMsg}`)
        }, 0)
      }
    },

    handleFilesExceed () {
      this.totalCount = 0
      this.$refs.vueDropzone.removeAllFiles(true)
      this.errorMessage = this.filesExceedMessage || this.$i18n.t('error.fileUpload.filesExceedLimitation', {maxNum: this.maxNumberOfFiles})
    },

    handleTotalSizeExceed (files) {
      this.handleFileValidationError(this.totalSizeExceedMessage || this.$i18n.t('hint.vueDropZone.fileTooBig'), files)
    },

    handleFileValidationError (message, files) {
      this.totalCount = 0
      const filesToRemove = this.normalizeFiles(files)
      if (filesToRemove.length) {
        filesToRemove.forEach(file => this.removeDropzoneFile(file))
      } else {
        this.$refs.vueDropzone.removeAllFiles(true)
      }
      this.errorMessage = message
    },

    removeDropzoneFile (file) {
      if (!this.$refs.vueDropzone || !file) return
      if (file.previewElement && !file.previewElement.parentNode) {
        file.previewElement = null
      }
      this.$refs.vueDropzone.removeFile(file)
    },

    appendErrorMessage (msg) {
      this.errorCount++
      const newline = this.errorMessage ? '\n' : ''
      this.errorMessage = this.errorMessage.concat(newline).concat(msg)
    },

    removeFile (file) {
      this.resetMessages()
      this.removeDropzoneFile(file)
    },

    clearMessages () {
      if (!this.totalCount) {
        this.errorMessage = ''
      }
      this.totalCount++
    },

    resetMessages () {
      this.uploadResult = ''
      this.errorMessage = ''
      this.dropzoneId = `dropzone-${new Date().getTime()}` + Math.random()
    },

    getFileSizeInBytes (file = {}) {
      const filesize = Number(file.filesize)
      if (Number.isFinite(filesize) && filesize >= 0) return filesize

      const size = Number(file.size)
      return Number.isFinite(size) && size >= 0 ? size : 0
    },

    normalizeFiles (files) {
      if (!files) return []
      if (Array.isArray(files)) return files
      if (typeof files.length === 'number') return Array.from(files)
      return [files]
    },

    isFileOverDropzoneMaxSize (file) {
      const maxFileSizeInBytes = Number(this.docUploadSize) * 1024 * 1024
      return maxFileSizeInBytes > 0 && this.getFileSizeInBytes(file) > maxFileSizeInBytes
    },

    shouldUseTotalSizeMessageForFileSizeError (file) {
      return this.useTotalSizeMessageForFileSizeError &&
        !!this.totalSizeExceedMessage &&
        this.isFileOverDropzoneMaxSize(file) &&
        this.isTotalSizeExceeded([file])
    },

    shouldUseTotalSizeMessageForFileSizeErrors (files = []) {
      const filesToValidate = this.normalizeFiles(files)
      return this.useTotalSizeMessageForFileSizeError &&
        !!this.totalSizeExceedMessage &&
        filesToValidate.some(file => this.isFileOverDropzoneMaxSize(file)) &&
        this.isTotalSizeExceeded(filesToValidate)
    },

    isDropzoneRejectedFile (file) {
      return !file ||
        file.status === 'error' ||
        file.accepted === false ||
        this.isFileOverDropzoneMaxSize(file)
    },

    getFilesEligibleForUpload (files = []) {
      return this.normalizeFiles(files).filter(file => !this.isDropzoneRejectedFile(file))
    },

    getFilesTotalSizeInBytes (files = []) {
      return this.normalizeFiles(files).reduce((total, file) => total + this.getFileSizeInBytes(file), 0)
    },

    getExistingFiles () {
      return Array.isArray(this.existingFiles) ? this.existingFiles : []
    },

    isMaxFilesExceeded (files = []) {
      return this.getExistingFiles().length + this.normalizeFiles(files).length > this.maxNumberOfFiles
    },

    isTotalSizeExceeded (files = []) {
      return this.maxTotalFileSizeInBytes > 0 &&
        this.getFilesTotalSizeInBytes(this.getExistingFiles()) + this.getFilesTotalSizeInBytes(files) > this.maxTotalFileSizeInBytes
    },

    filesAddedAction (files) {
      const filesAdded = this.normalizeFiles(files)
      const filesToUpload = this.getFilesEligibleForUpload(filesAdded)
      this.resetMessages()
      this.successCount = 0
      this.errorCount = 0
      this.totalCount = filesAdded.length
      this.uploadedFiles = []
      if (this.shouldUseTotalSizeMessageForFileSizeErrors(filesAdded)) {
        setTimeout(() => this.handleTotalSizeExceed(filesAdded), 0)
        return
      }
      this.totalCount = filesToUpload.length
      if (!filesToUpload.length) {
        return
      }
      if (this.isMaxFilesExceeded(filesToUpload)) {
        setTimeout(() => this.handleFileValidationError(this.filesExceedMessage || this.$i18n.t('error.fileUpload.filesExceedLimitation', {maxNum: this.maxNumberOfFiles}), filesToUpload), 0)
        return
      }
      if (this.isTotalSizeExceeded(filesToUpload)) {
        setTimeout(() => this.handleTotalSizeExceed(filesToUpload), 0)
        return
      }
      if (this.dropzoneAutoProcess) {
        EventBus.$emit('module_loading_startLoading')
        return
      }
      if (filesToUpload.length > this.maxNumberOfFiles) {
        // @TODO <Jackson_Wang@epam.com> to fix file-added v.s. files-added event sequence issue from root.
        setTimeout(this.handleFilesExceed, 200)
      } else {
        setTimeout(this.$refs.vueDropzone.processQueue, 200)
        EventBus.$emit('module_loading_startLoading')
      }
    },

    uploadSuccess (file) {
      this.removeDropzoneFile(file)
      if (file.xhr) {
        const xhrResponse = file.xhr.response
        const xhrStatus = file.xhr.status
        if (xhrStatus >= 200 && xhrStatus <= 300 && xhrResponse) {
          const newItem = JSON.parse(xhrResponse).data[0]
          if (!newItem.isNewRecord) {
            const duplicateNameMessage = this.$i18n.t('hint.vueDropZone.fileUploadError', [newItem.filename]) + ': ' +
              this.$i18n.t('hint.vueDropZone.duplicateFileName')
            this.appendErrorMessage(duplicateNameMessage)
            return false
          }
          if (this.currentUser.id === newItem.createdBy) {
            newItem.creator = lodash.cloneDeep(this.currentUser)
          }
          this.uploadedFiles.push(newItem)
          EventBus.$emit(`controls.hkldDropzone.newItem.${this.sourceId}`, newItem)
        }
      }
      this.successCount++
      EventBus.$emit('controls-hkld-dropzone-complete')
    },

    setupListener () {
      if (this.sourceId) {
        EventBus.$off(`controls.hkldDropzone.deleteItem.${this.prevSourceId}`)
        EventBus.$on(`controls.hkldDropzone.deleteItem.${this.sourceId}`, (deleteItem) => {
          this.removeFile(deleteItem)
        })
        this.prevSourceId = this.sourceId
      }
    },
  },
}
</script>

<style lang="scss">
.dropzone.dz-started .dz-message {
  display: block;
}

.dropzone{
  padding: 2px;
  border: 0;
}

.dz-default {
  background-color: white;
  // border: 2px dashed #e0e0e0;
}

.dz-complete, .dz-remove, .dz-error-message {
  display: none;
}

.upload-img {
  width: 40px;
  height: 40px;
}
.upload-text {
  color: #6E7A91;
}

.hkld-dropzone__note {
  color: #8A8A8A;
  font-size: 14px;
  line-height: 20px;
  margin-top: 4px;
  white-space: normal;
}

.hkld-dropzone__error-message {
  color: #FF3B30;
  white-space: pre-line;
}
</style>
